/** Electricity prices: the company's own prices. One price for every customer, and prices for chosen utilities
 * (up to the company's limit), picked from the utility registry. Solar OS's price values are never shown here. */
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Pin, Plus, Search, Trash2, Undo2, Users, X, Zap } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { api, errorMessage, unwrap, type S } from "../api/client";
import { useOrg, useOrgId } from "../api/org";
import { useOrganization } from "../api/queries";
import { CURRENCIES, PriceFields, blankTariff, energySummary, exportSummary, fixedSummary, priceProblems, usePriceText, type Tariff } from "../components/PriceFields";
import { Alert, Badge, Button, Card, Empty, Field, NumberInput, PageHead, Select, Spinner, cx, useToast } from "../components/ui";
import { useIntlShape, useT } from "../i18n/I18nProvider";
import { priceCurrency } from "../lib/currency";
import { countryName, regionName, servedWhere } from "../lib/regions";
import { useDebounced } from "./admin/Orgs";

type Override = S["ElectricityPriceOverride"];
type Utility = S["UtilitySummary"];
type Target = { utility: Utility | null; existing?: Override };
const PAGE = 40;

function PriceSummary({ t }: { t: Tariff }) {
  const text = usePriceText();
  return (
    <span className="small muted">
      {[energySummary(t, text), fixedSummary(t, text), exportSummary(t, text)].filter((x) => x !== "—").join(" · ")}
    </span>
  );
}

function PriceBadge({ utility }: { utility: Utility }) {
  const t = useT();
  return utility.solar_os_price ? <Badge tone="info">{t("rules.utilities.badge.solarOs")}</Badge> : <Badge tone="warn">{t("rules.utilities.badge.none")}</Badge>;
}

/** Where a utility serves, in the reader's language. */
function useWhere(): (country: string, regions: string[]) => string {
  const { locale } = useIntlShape();
  return (country, regions) => servedWhere(country, regions, locale);
}

function Meter({ used, limit }: { used: number; limit: number }) {
  const t = useT();
  const full = used >= limit;
  return (
    <div className="usage-meter" aria-label={t("rules.utilities.meter.aria", { used, limit })}>
      <div className="row" style={{ justifyContent: "space-between" }}>
        <b>{t("rules.utilities.meter.used", { used, limit })}</b>
        <span className={cx("small", full ? "warn-text" : "faint")}>{full ? t("rules.utilities.meter.full") : t("rules.utilities.meter.left", { count: limit - used })}</span>
      </div>
      <div className="meter-track">
        <div className={cx("meter-fill", full && "full")} style={{ width: `${limit ? Math.min(100, (used / limit) * 100) : 100}%` }} />
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ drawer
function PriceDrawer({ orgId, target, company, onClose, onSaved }: { orgId: string; target: Target; company: S["Organization"] | undefined; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const tr = useT();
  const where = useWhere();
  const { utility, existing } = target;
  const name = utility ? utility.name : tr("rules.utilities.everyone.title");
  const [t, setT] = useState<Tariff>(() =>
    structuredClone(existing?.structure ?? blankTariff(utility ? tr("rules.utilities.tariffName.utility", { utility: utility.name }) : tr("rules.utilities.tariffName.everyone"), priceCurrency(utility?.country, company))),
  );
  const [escalation, setEscalation] = useState<number | null>(existing?.escalation != null ? Math.round(existing.escalation * 1000) / 10 : null);
  const [tried, setTried] = useState(false);
  const problems = priceProblems(t);
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        api.PUT("/v1/orgs/{org_id}/electricity-prices", {
          params: { path: { org_id: orgId } },
          body: { ...(utility ? { utility_id: utility.id } : { scope: "*" }), structure: { ...t, utility: utility?.name ?? null }, escalation: escalation == null ? null : escalation / 100 },
        }),
      ),
    onSuccess: () => {
      toast(utility ? tr("rules.utilities.saved.utility", { utility: utility.name }) : tr("rules.utilities.saved.everyone"));
      onSaved();
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const submit = () => {
    setTried(true);
    if (!Object.keys(problems).length) save.mutate();
  };
  return (
    <div className="drawer-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside className="drawer" role="dialog" aria-modal="true" aria-label={tr("rules.utilities.drawer.aria", { name })}>
        <header className="drawer-head">
          <div>
            <div className="small faint">{existing ? tr("rules.utilities.drawer.edit") : tr("rules.utilities.drawer.set")}</div>
            <h2>{name}</h2>
            <div className="row small muted" style={{ gap: 8, marginTop: 4, flexWrap: "wrap" }}>
              {utility ? (
                <>
                  <span dir="auto">{where(utility.country, utility.regions)}</span>
                  <PriceBadge utility={utility} />
                </>
              ) : (
                <span>{tr("rules.utilities.drawer.everyoneHint")}</span>
              )}
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} aria-label={tr("rules.action.close")}>
            <X />
          </button>
        </header>
        <div className="drawer-body stack">
          <Alert tone="info">
            {utility?.solar_os_price
              ? tr("rules.utilities.drawer.replaces")
              : utility
                ? tr("rules.utilities.drawer.noSolarOs")
                : tr("rules.utilities.drawer.replacesEveryone")}
          </Alert>
          <Field label={tr("rules.utilities.drawer.currency")}>
            <Select value={t.currency} onChange={(v) => setT({ ...t, currency: v })} options={CURRENCIES.includes(t.currency) ? CURRENCIES : [t.currency, ...CURRENCIES]} />
          </Field>
          <PriceFields value={t} onChange={setT} showErrors={tried} />
          <Field label={tr("rules.utilities.drawer.escalation.label")} hint={tr("rules.utilities.drawer.escalation.hint")}>
            <NumberInput value={escalation} step={0.1} min={-50} max={100} placeholder={tr("rules.utilities.drawer.escalation.placeholder", { example: 3 })} suffix={tr("rules.utilities.drawer.escalation.suffix")} onChange={setEscalation} />
          </Field>
        </div>
        <footer className="drawer-foot">
          {tried && Object.keys(problems).length > 0 && <span className="field-error">{tr("rules.utilities.drawer.fix")}</span>}
          <Button variant="ghost" onClick={onClose}>
            {tr("rules.action.cancel")}
          </Button>
          <Button variant="primary" loading={save.isPending} onClick={submit}>
            {tr("rules.utilities.drawer.save")}
          </Button>
        </footer>
      </aside>
    </div>
  );
}

// ------------------------------------------------------------------ browse
function Browse({ overrides, atLimit, canEdit, onPick }: { overrides: Map<string, Override>; atLimit: boolean; canEdit: boolean; onPick: (u: Utility) => void }) {
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [region, setRegion] = useState("");
  const [active, setActive] = useState(0);
  const t = useT();
  const intl = useIntlShape();
  const where = useWhere();
  const country_ = (code: string) => countryName(code, intl.locale);
  const text = useDebounced(q.trim(), 200);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const regions = useQuery({ queryKey: ["utility-regions"], queryFn: () => unwrap(api.GET("/v1/utilities/regions")), staleTime: 300_000 });
  const countries = useMemo(() => (regions.data?.items ?? []).filter((r) => !r.region).sort((a, b) => b.count - a.count), [regions.data]);
  const countryRegions = useMemo(
    () => (regions.data?.items ?? []).filter((r) => r.region && r.country === country).sort((a, b) => regionName(a.region!).localeCompare(regionName(b.region!))),
    [regions.data, country],
  );
  const results = useInfiniteQuery({
    queryKey: ["utilities", text, country, region],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      unwrap(api.GET("/v1/utilities", { params: { query: { q: text || undefined, country: country || undefined, region: region || undefined, limit: PAGE, offset: pageParam } } })),
    getNextPageParam: (last, pages) => (pages.length * PAGE < last.total ? pages.length * PAGE : undefined),
    placeholderData: keepPreviousData,
  });
  const items = results.data?.pages.flatMap((p) => p.items) ?? [];
  const total = results.data?.pages[0]?.total ?? 0;
  useEffect(() => setActive(0), [text, country, region]);
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "/" && !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)) {
        e.preventDefault();
        input.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  // Loads the next page when the end of the list scrolls into view.
  const sentinel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = sentinel.current;
    if (!node || !results.hasNextPage) return;
    const observer = new IntersectionObserver((entries) => entries[0].isIntersecting && !results.isFetchingNextPage && results.fetchNextPage());
    observer.observe(node);
    return () => observer.disconnect();
  }, [results.hasNextPage, results.isFetchingNextPage, results]);
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const next = Math.max(0, Math.min(items.length - 1, active + (e.key === "ArrowDown" ? 1 : -1)));
      setActive(next);
      list.current?.children[next]?.scrollIntoView({ block: "nearest" });
    } else if (e.key === "Enter" && items[active]) {
      e.preventDefault();
      const u = items[active];
      if (canEdit && (overrides.has(u.id) || !atLimit)) onPick(u);
    } else if (e.key === "Escape") setQ("");
  };
  return (
    <Card title={t("rules.utilities.find.title")} description={t("rules.utilities.find.description")} pad={false}>
      <div className="browse-tools">
        <div className="input-icon grow">
          <Search />
          <input
            ref={input}
            className="input"
            placeholder={t("rules.utilities.find.placeholder", { example: "BC Hydro" })}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            role="combobox"
            aria-expanded="true"
            aria-controls="utility-results"
            aria-activedescendant={items[active] ? `utility-${items[active].id}` : undefined}
          />
          {q ? (
            <button className="input-clear" aria-label={t("rules.utilities.find.clear")} onClick={() => setQ("")}>
              <X size={14} />
            </button>
          ) : (
            <kbd className="kbd-hint">/</kbd>
          )}
        </div>
        <div className="filter-chips" role="group" aria-label={t("rules.utilities.find.country")}>
          <button className={cx("filter-chip", !country && "on")} onClick={() => (setCountry(""), setRegion(""))}>
            {t("rules.utilities.find.all")}
          </button>
          {countries.map((c) => (
            <button key={c.country} className={cx("filter-chip", country === c.country && "on")} onClick={() => (setCountry(c.country), setRegion(""))}>
              {country_(c.country)} <span className="count">{intl.formatNumber(c.count)}</span>
            </button>
          ))}
        </div>
        {country && countryRegions.length > 0 && (
          <div style={{ minWidth: 200 }}>
            <Select
              ariaLabel={t("rules.utilities.find.region")}
              value={region}
              onChange={setRegion}
              options={[
                { value: "", label: t("rules.utilities.find.allOf", { country: country_(country) }) },
                ...countryRegions.map((r) => ({ value: r.region!, label: t("rules.utilities.find.regionCount", { region: regionName(r.region!), count: r.count }) })),
              ]}
            />
          </div>
        )}
      </div>
      <div className="browse-count small faint">
        {results.isFetching && !results.isFetchingNextPage ? t("rules.utilities.find.searching") : t("rules.utilities.find.count", { count: total })}
        {canEdit && <span className="hide-sm"> · {t("rules.utilities.find.keys")}</span>}
      </div>
      {results.isLoading ? (
        <Spinner />
      ) : results.error ? (
        <div className="card-body">
          <Alert tone="danger">{errorMessage(results.error)}</Alert>
        </div>
      ) : !items.length ? (
        <Empty icon={<Search />} title={t("rules.utilities.find.empty.title")}>
          {t("rules.utilities.find.empty.body")}
        </Empty>
      ) : (
        <ul className="utility-list" id="utility-results" role="listbox" ref={list}>
          {items.map((u, i) => {
            const mine = overrides.get(u.id);
            const blocked = !mine && atLimit;
            return (
              <li
                key={u.id}
                id={`utility-${u.id}`}
                role="option"
                aria-selected={i === active}
                className={cx("utility-row", i === active && "active")}
                onMouseEnter={() => setActive(i)}
              >
                <div className="utility-main">
                  <div className="cell-main">{u.name}</div>
                  <div className="row small muted" style={{ gap: 8, flexWrap: "wrap" }}>
                    <span dir="auto">{where(u.country, u.regions)}</span>
                    {mine ? <Badge tone="ok">{t("rules.utilities.badge.yours")}</Badge> : <PriceBadge utility={u} />}
                  </div>
                </div>
                {canEdit && (
                  <Button size="sm" icon={mine ? <Pencil /> : <Plus />} disabled={blocked} title={blocked ? t("rules.utilities.find.blocked") : undefined} onClick={() => onPick(u)}>
                    {mine ? t("rules.action.edit") : t("rules.utilities.drawer.set")}
                  </Button>
                )}
              </li>
            );
          })}
          <div ref={sentinel} />
          {results.isFetchingNextPage && (
            <li className="utility-row small faint" aria-hidden>
              {t("rules.utilities.find.loadingMore")}
            </li>
          )}
        </ul>
      )}
    </Card>
  );
}

// -------------------------------------------------------------------- page
export function ElectricityPrices() {
  const orgId = useOrgId();
  const { can } = useOrg();
  const canEdit = can("settings_rules", "edit");
  const qc = useQueryClient();
  const toast = useToast();
  const company = useOrganization(orgId);
  const t = useT();
  const intl = useIntlShape();
  const where = useWhere();
  const [target, setTarget] = useState<Target | null>(null);
  const [removed, setRemoved] = useState<Override | null>(null);
  const key = ["electricity-prices", orgId];
  const prices = useQuery({ queryKey: key, queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/electricity-prices", { params: { path: { org_id: orgId } } })) });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: key });
    qc.invalidateQueries({ queryKey: ["utilities"] });
  };
  const remove = useMutation({
    mutationFn: (o: Override) => unwrap(api.DELETE("/v1/orgs/{org_id}/electricity-prices/{override_id}", { params: { path: { org_id: orgId, override_id: o.id } } })).then(() => o),
    onSuccess: (o) => {
      setRemoved(o);
      refresh();
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const undo = useMutation({
    mutationFn: (o: Override) =>
      unwrap(
        api.PUT("/v1/orgs/{org_id}/electricity-prices", {
          params: { path: { org_id: orgId } },
          body: { ...(o.utility ? { utility_id: o.utility.id } : { scope: o.scope }), structure: o.structure, escalation: o.escalation },
        }),
      ),
    onSuccess: () => {
      setRemoved(null);
      refresh();
      toast(t("rules.utilities.restored"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  useEffect(() => {
    if (!removed) return;
    const timer = window.setTimeout(() => setRemoved(null), 12000);
    return () => window.clearTimeout(timer);
  }, [removed]);

  const data = prices.data;
  const everyone = data?.items.find((o) => o.scope === "*" && o.structure);
  const mine = (data?.items ?? []).filter((o) => o.utility && o.structure);
  const byUtility = useMemo(() => new Map(mine.map((o) => [o.utility!.id, o])), [mine]);
  const atLimit = !!data && data.used >= data.limit;

  return (
    <>
      <PageHead
        title={t("rules.utilities.page.title")}
        description={t("rules.utilities.page.description")}
      />
      {removed && (
        <div className="undo-bar" role="status">
          <span>
            {intl.formatMessage(
              { id: removed.utility ? "rules.utilities.removed.utility" : "rules.utilities.removed.everyone" },
              { b: (c) => <b>{c}</b>, utility: removed.utility?.name ?? "" },
            )}
          </span>
          <Button size="sm" icon={<Undo2 />} loading={undo.isPending} onClick={() => undo.mutate(removed)}>
            {t("rules.utilities.undo")}
          </Button>
        </div>
      )}
      {prices.isLoading ? (
        <Spinner />
      ) : prices.error || !data ? (
        <Alert tone="danger">{errorMessage(prices.error)}</Alert>
      ) : (
        <div className="stack">
          <div className="prices-top">
            <Card
              title={
                <span className="row" style={{ gap: 8 }}>
                  <Users size={16} /> {t("rules.utilities.everyone.title")}
                </span>
              }
              description={t("rules.utilities.everyone.description")}
              actions={
                canEdit &&
                (everyone ? (
                  <div className="row" style={{ gap: 6 }}>
                    <Button size="sm" icon={<Pencil />} onClick={() => setTarget({ utility: null, existing: everyone })}>
                      {t("rules.action.edit")}
                    </Button>
                    <Button size="sm" variant="ghost" icon={<Trash2 />} aria-label={t("rules.utilities.everyone.remove")} loading={remove.isPending && remove.variables?.id === everyone.id} onClick={() => remove.mutate(everyone)} />
                  </div>
                ) : (
                  <Button size="sm" variant="primary" icon={<Plus />} onClick={() => setTarget({ utility: null })}>
                    {t("rules.utilities.everyone.set")}
                  </Button>
                ))
              }
            >
              {everyone?.structure ? (
                <PriceSummary t={everyone.structure} />
              ) : (
                <span className="small faint">{t("rules.utilities.everyone.notSet")}</span>
              )}
            </Card>
            <Card title={t("rules.utilities.limit.title")} description={data.limit_is_default ? t("rules.utilities.limit.plan") : t("rules.utilities.limit.custom")}>
              <Meter used={data.used} limit={data.limit} />
            </Card>
          </div>

          <Card
            title={
              <span className="row" style={{ gap: 8 }}>
                <Pin size={16} /> {t("rules.utilities.mine.title")}
              </span>
            }
            pad={false}
          >
            {atLimit && (
              <div className="card-body" style={{ paddingBottom: 0 }}>
                <Alert tone="warn" title={t("rules.utilities.mine.full.title")}>
                  {t("rules.utilities.mine.full.body")}
                </Alert>
              </div>
            )}
            {!mine.length ? (
              <Empty icon={<Zap />} title={t("rules.utilities.mine.empty.title")}>
                {t("rules.utilities.mine.empty.body")}
              </Empty>
            ) : (
              <ul className="utility-list pinned">
                {mine.map((o) => (
                  <li key={o.id} className="utility-row">
                    <div className="utility-main">
                      <div className="cell-main">{o.utility!.name}</div>
                      <div className="row small muted" style={{ gap: 8, flexWrap: "wrap" }}>
                        <span dir="auto">{where(o.utility!.country, o.utility!.regions)}</span>
                        <PriceSummary t={o.structure!} />
                        <span className="faint">{t("rules.utilities.mine.updated", { date: intl.formatDate(o.updated_at, { year: "numeric", month: "short", day: "numeric" }) })}</span>
                      </div>
                    </div>
                    {canEdit && (
                      <div className="row" style={{ gap: 6 }}>
                        <Button size="sm" icon={<Pencil />} onClick={() => setTarget({ utility: o.utility!, existing: o })}>
                          {t("rules.action.edit")}
                        </Button>
                        <Button size="sm" variant="ghost" icon={<Trash2 />} aria-label={t("rules.utilities.mine.remove", { utility: o.utility!.name })} loading={remove.isPending && remove.variables?.id === o.id} onClick={() => remove.mutate(o)} />
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Browse overrides={byUtility} atLimit={atLimit} canEdit={canEdit} onPick={(u) => setTarget({ utility: u, existing: byUtility.get(u.id) })} />
        </div>
      )}
      {target && (
        <PriceDrawer
          orgId={orgId}
          target={target}
          company={company.data}
          onClose={() => setTarget(null)}
          onSaved={() => {
            setTarget(null);
            refresh();
          }}
        />
      )}
    </>
  );
}
